import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge, Spinner, Alert, Pagination } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faUsers, faPlus, faToggleOn, faToggleOff, faEye } from '@fortawesome/free-solid-svg-icons';
import ClassroomService from '../../services/classroom.service';
import GradeService from '../../services/grade.service';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useToast } from '../../contexts/ToastContext';

const Classes = () => {
  const { user } = useAuth();
  const { selectedYear } = useAcademicYear();
  const { showToast } = useToast();
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState({
    grade: '',
    name: '',
    shift: '',
    year: '',
    active: true
  });
  const [filters, setFilters] = useState({
    grade: '',
    shift: '',
    active: ''
  });

  const fetchGrades = async () => {
    try {
      const data = await GradeService.getAll();
      setGrades(data.results || []);
    } catch (err) {
      setError('Erro ao carregar séries. Tente novamente.');
    }
  };

  const fetchClasses = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = { year: selectedYear, page };

      // Adicionar filtros aos params
      if (filters.grade) params.grade = filters.grade;
      if (filters.shift) params.shift = filters.shift;
      if (filters.active !== '') params.active = filters.active;

      const data = await ClassroomService.getAll(params);
      setClasses(data.results || []);
      setPagination({
        count: data.count || 0,
        next: data.next,
        previous: data.previous,
        currentPage: page,
        totalPages: Math.ceil((data.count || 0) / 10)
      });
    } catch (err) {
      setError('Erro ao carregar turmas. Tente novamente.');
      console.error('Error fetching classes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    fetchClasses(page);
  };

  useEffect(() => {
    fetchGrades();
  }, []);

  useEffect(() => {
    fetchClasses(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      grade: '',
      shift: '',
      active: ''
    });
  };

  const handleEdit = (classItem) => {
    setSelectedClass(classItem);
    setFormData({
      grade: classItem.grade,
      name: classItem.name,
      shift: classItem.shift,
      year: classItem.year,
      active: classItem.active
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedClass(null);
    setFormData({
      grade: '',
      name: '',
      shift: '',
      year: '',
      active: true
    });
  };

  const handleView = (classItem) => {
    setSelectedClass(classItem);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedClass(null);
  };

  const handleOpenCreateModal = () => {
    setFormData({
      grade: '',
      name: '',
      shift: 'Manha',
      year: new Date().getFullYear().toString(),
      active: true
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      grade: '',
      name: '',
      shift: '',
      year: '',
      active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        grade: formData.grade,
        name: formData.name,
        shift: formData.shift,
        year: parseInt(formData.year),
        active: formData.active
      };
      await ClassroomService.partialUpdate(selectedClass.id, payload);
      await fetchClasses(pagination.currentPage);
      handleCloseModal();
      showToast('Turma atualizada com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating class:', err);
      showToast('Erro ao atualizar turma. Tente novamente.', 'danger');
    }
  };

  const handleCreate = async () => {
    try {
      // Busca a primeira escola ativa do usuário
      const activeSchool = user?.school_users?.find(su => su.active);
      if (!activeSchool) {
        showToast('Nenhuma escola ativa encontrada.', 'warning');
        return;
      }

      const payload = {
        grade: formData.grade,
        name: formData.name,
        shift: formData.shift,
        year: parseInt(formData.year),
        active: formData.active,
        school: activeSchool.school.id
      };
      await ClassroomService.create(payload);
      await fetchClasses(pagination.currentPage);
      handleCloseCreateModal();
      showToast('Turma criada com sucesso!', 'success');
    } catch (err) {
      console.error('Error creating class:', err);
      showToast('Erro ao criar turma. Tente novamente.', 'danger');
    }
  };

  const getShiftLabel = (shift) => {
    const shifts = {
      'Manha': 'Manhã',
      'Tarde': 'Tarde',
      'Noite': 'Noite',
      'Integral': 'Integral'
    };
    return shifts[shift] || shift;
  };

  const getGradeName = (gradeId) => {
    const grade = grades.find(g => g.id === gradeId);
    if (!grade) return gradeId;
    return grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name;
  };

  const getEducationLevelLabel = (level) => {
    const levels = {
      'educacao_infantil': 'Educação Infantil',
      'ensino_fundamental_i': 'Ensino Fundamental I',
      'ensino_fundamental_ii': 'Ensino Fundamental II',
      'ensino_medio': 'Ensino Médio'
    };
    return levels[level] || level;
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Gestão de Turmas - {selectedYear}</h4>
          <p className="mb-0">Visualize e edite as turmas da escola</p>
        </div>
      </div>

      <Card border="light" className="shadow-sm mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Série</Form.Label>
                <Form.Select
                  name="grade"
                  value={filters.grade}
                  onChange={handleFilterChange}
                >
                  <option value="">Todas as séries</option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Turno</Form.Label>
                <Form.Select
                  name="shift"
                  value={filters.shift}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos os turnos</option>
                  <option value="Manha">Manhã</option>
                  <option value="Tarde">Tarde</option>
                  <option value="Noite">Noite</option>
                  <option value="Integral">Integral</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="active"
                  value={filters.active}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos</option>
                  <option value="true">Ativas</option>
                  <option value="false">Inativas</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={5} className="d-flex align-items-end">
              <Button variant="secondary" onClick={handleClearFilters} className="w-100">
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col xs={12} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    Lista de Turmas
                  </h5>
                </Col>
                <Col className="text-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenCreateModal}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Nova Turma
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </Spinner>
                </div>
              )}
              {error && (
                <Alert variant="danger" className="m-3">
                  {error}
                </Alert>
              )}
              {!loading && !error && classes.length === 0 && (
                <div className="text-center py-5">
                  <p className="text-muted">Nenhuma turma encontrada.</p>
                </div>
              )}
              {!loading && !error && classes.length > 0 && (
                <Table responsive hover className="align-items-center table-flush mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Turma</th>
                      <th>Turno</th>
                      <th>Ano Letivo</th>
                      <th>Escola</th>
                      <th>Alunos</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((classItem) => (
                      <tr key={classItem.id}>
                        <td className="fw-bold">{getGradeName(classItem.grade)} {classItem.name}</td>
                        <td>{getShiftLabel(classItem.shift)}</td>
                        <td>{classItem.year}</td>
                        <td>{classItem.school_name}</td>
                        <td>
                          <Badge bg="info">{classItem.students_count}</Badge>
                        </td>
                        <td>
                          {classItem.active ? (
                            <Badge bg="success">
                              <FontAwesomeIcon icon={faToggleOn} className="me-1" />
                              Ativa
                            </Badge>
                          ) : (
                            <Badge bg="secondary">
                              <FontAwesomeIcon icon={faToggleOff} className="me-1" />
                              Inativa
                            </Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleView(classItem)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleEdit(classItem)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
            {!loading && !error && classes.length > 0 && (
              <Card.Footer className="px-3 border-0 d-flex justify-content-center">
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.previous}
                  />
                  {[...Array(pagination.totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === pagination.currentPage}
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.next}
                  />
                </Pagination>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={handleCloseModal} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Turma</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Série</Form.Label>
              <Form.Select
                name="grade"
                value={formData.grade}
                onChange={handleInputChange}
              >
                <option value="">Selecione a série</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nome da Turma</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Turma A"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Turno</Form.Label>
              <Form.Select
                name="shift"
                value={formData.shift}
                onChange={handleInputChange}
              >
                <option value="">Selecione o turno</option>
                <option value="Manha">Manhã</option>
                <option value="Tarde">Tarde</option>
                <option value="Noite">Noite</option>
                <option value="Integral">Integral</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ano Letivo</Form.Label>
              <Form.Control
                type="number"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                placeholder="Ex: 2024"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Turma</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Série</Form.Label>
              <Form.Select
                name="grade"
                value={formData.grade}
                onChange={handleInputChange}
                required
              >
                <option value="">Selecione a série</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.education_level ? `${grade.name} - ${getEducationLevelLabel(grade.education_level)}` : grade.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nome da Turma</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Ex: Turma A"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Turno</Form.Label>
              <Form.Select
                name="shift"
                value={formData.shift}
                onChange={handleInputChange}
                required
              >
                <option value="">Selecione o turno</option>
                <option value="Manha">Manhã</option>
                <option value="Tarde">Tarde</option>
                <option value="Noite">Noite</option>
                <option value="Integral">Integral</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ano Letivo</Form.Label>
              <Form.Control
                type="number"
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                placeholder="Ex: 2024"
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!formData.grade || !formData.name || !formData.shift || !formData.year}
          >
            Criar Turma
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showViewModal} onHide={handleCloseViewModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Visualizar Turma</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Row className="mb-2">
            <Col md={12}><strong>Turma:</strong> {selectedClass ? `${getGradeName(selectedClass.grade)} ${selectedClass.name}` : '-'}</Col>
          </Row>
          <Row className="mb-2">
            <Col md={6}><strong>Turno:</strong> {selectedClass ? getShiftLabel(selectedClass.shift) : '-'}</Col>
            <Col md={6}><strong>Ano Letivo:</strong> {selectedClass?.year || '-'}</Col>
          </Row>
          <Row className="mb-2">
            <Col md={12}><strong>Escola:</strong> {selectedClass?.school_name || '-'}</Col>
          </Row>
          <Row className="mb-2">
            <Col md={6}><strong>Alunos:</strong> {selectedClass?.students_count || 0}</Col>
            <Col md={6}><strong>Status:</strong> {selectedClass?.active ? 'Ativa' : 'Inativa'}</Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseViewModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Classes;
